import React, { useEffect } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { TextField } from '@material-ui/core';
import {
  Grid, Button, Card, CardContent, Alert,
  Dialog, DialogContent, DialogTitle, IconButton, Box,
  Checkbox, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, Form, FormikProps } from 'formik';
import { useSnackbar } from 'notistack';

import { useAuth } from '../../../contexts/auth';
import callService, {
  getAdjustmentDaysAllowed,
  isCallWithinAdjustmentWindow
} from '../../../services/callService';
import { getAddressByGoogle, viacep } from '../../../services/geolocateService';
import {
  phoneNumber, toBrazilDateTime, CepMask, CurrencyFormatCustom
} from '../../../components/mask';
import { convertStatusToBrazilian } from '../../../models/enums/statusCall';
import PaymentType from '../../../models/enums/paymentType';
import Loading from '../../../components/loading';
import Address from '../../../models/interfaces/address';

interface ParamTypes {
  id: string;
}

interface Props {
  open: boolean;
  callId: string;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onUpdated: (destinationIndex: number, nextAddress: string) => void;
}

interface DestinationForm {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  complement: string;
  comments: string;
  zipCode: string;
  receiverName: string;
  receiverPhone: string;
  currentAddress: string;
  value: number;
  valueBack: number;
  subsidiaryFeeValue: number;
  subsidiaryFeeValueBack: number;
  distanceInfo: string;
  durationInfo: string;
}

interface FormValues {
  destinations: DestinationForm[];
}

interface RouteQuote {
  addressKey: string;
  distance: number;
  distanceInfo: string | null;
  duration: number | null;
  durationInfo: string | null;
  latitude: number;
  longitude: number;
  placeId: string | null;
}

const emptyDestination = (): DestinationForm => ({
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  complement: '',
  comments: '',
  zipCode: '',
  receiverName: '',
  receiverPhone: '',
  currentAddress: '',
  value: 0,
  valueBack: 0,
  subsidiaryFeeValue: 0,
  subsidiaryFeeValueBack: 0,
  distanceInfo: '',
  durationInfo: ''
});

const sameText = (left: string, right: string) => (left ?? '').trim() === (right ?? '').trim();

const toMoney = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Number(parsed.toFixed(2));
};

const sameMoney = (left: number, right: number) => toMoney(left) === toMoney(right);

const formatDestinationAddress = (destination: DestinationForm) => [
  [destination.street, destination.number].filter((part) => part.trim()).join(', '),
  destination.neighborhood,
  [destination.city, destination.state].filter((part) => part.trim()).join(' - ')
].filter((part) => part.trim()).join(', ');

const formatRouteDistance = (destination: any) => {
  if (destination?.distanceInfo) return String(destination.distanceInfo);
  const meters = Number(destination?.distance);
  if (!Number.isFinite(meters) || meters <= 0) return '';
  return `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
};

const formatRouteDuration = (destination: any) => {
  if (destination?.durationInfo) return String(destination.durationInfo);
  const seconds = Number(destination?.duration);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  return `${Math.round(seconds / 60)} min`;
};

const toFormDestination = (destination: any): DestinationForm => ({
  street: destination?.addressObj?.street ?? '',
  number: destination?.addressObj?.number ?? '',
  neighborhood: destination?.addressObj?.neighborhood ?? '',
  city: destination?.addressObj?.city ?? '',
  state: destination?.addressObj?.state ?? '',
  complement: destination?.complement ?? '',
  comments: destination?.comments ?? '',
  zipCode: '',
  receiverName: destination?.receiverName ?? '',
  receiverPhone: destination?.receiverPhone ?? '',
  currentAddress: destination?.address ?? '',
  value: toMoney(destination?.tariff?.value),
  valueBack: toMoney(destination?.tariff?.valueBack),
  subsidiaryFeeValue: toMoney(destination?.tariff?.subsidiaryFeeValue),
  subsidiaryFeeValueBack: toMoney(destination?.tariff?.subsidiaryFeeValueBack),
  distanceInfo: formatRouteDistance(destination),
  durationInfo: formatRouteDuration(destination)
});

const toCallDate = (value: any): Date => {
  if (!value) return new Date(NaN);
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

export const AdjustmentForm = ({
  open, callId, onClose, onUpdated
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, subsidiaryId, isReadOnly } = useAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  const [savingIndex, setSavingIndex] = React.useState<number | null>(null);
  const [clientName, setClientName] = React.useState('');
  const [createdAt, setCreatedAt] = React.useState<Date | null>(null);
  const [statusLabel, setStatusLabel] = React.useState('');
  const [paymentValue, setPaymentValue] = React.useState<number | null>(null);
  const [eligible, setEligible] = React.useState(false);
  const [hasBack, setHasBack] = React.useState(false);
  const [savedHasBack, setSavedHasBack] = React.useState(false);
  const [routeQuote, setRouteQuote] = React.useState<RouteQuote | null>(null);
  const [recalculatingIndex, setRecalculatingIndex] = React.useState<number | null>(null);
  const [clientId, setClientId] = React.useState('');
  const [driverCategory, setDriverCategory] = React.useState<number | null>(null);
  const [initialValues, setInitialValues] = React.useState<FormValues>({ destinations: [] });
  const [baseline, setBaseline] = React.useState<DestinationForm[]>([]);
  const daysAllowed = getAdjustmentDaysAllowed(Boolean(user?.claims?.admin));
  const withinWindow = createdAt ? isCallWithinAdjustmentWindow(createdAt, daysAllowed) : false;
  const canEdit = eligible && withinWindow;

  useEffect(() => {
    if (!open) return undefined;
    if (isReadOnly) {
      onClose();
      return undefined;
    }

    let active = true;
    setRouteQuote(null);
    setIsLoading(true);
    callService.getById(callId).then((data) => {
      if (!active) return;
      if (!data) {
        enqueueSnackbar('Entrega não encontrada', { variant: 'error' });
        onClose();
        return;
      }
      if (!user?.claims?.admin && subsidiaryId && data.subsidiary && data.subsidiary !== subsidiaryId) {
        enqueueSnackbar('Esta entrega não pertence à franquia selecionada', { variant: 'error' });
        onClose();
        return;
      }
      const destinations = (data.destinations ?? []).map(toFormDestination);
      const callDate = toCallDate(data.createdAt);
      setClientName(data.clientName || data.origin?.responsible || 'Não informado');
      setCreatedAt(callDate);
      setStatusLabel(convertStatusToBrazilian(data.status).description);
      setPaymentValue(typeof data.payment?.value === 'number' ? data.payment.value : null);
      const withReturn = Boolean(data.hasBack);
      setHasBack(withReturn);
      setSavedHasBack(withReturn);
      setClientId(data.origin?.clientId || data.clientKey || '');
      setDriverCategory(data.categoryDriver ?? data.destinations?.[0]?.tariff?.categoryDriver ?? null);
      setEligible(data.payment?.type === PaymentType.Faturado && (data.destinations ?? []).length === 1);
      setInitialValues({ destinations });
      setBaseline(destinations);
    }).catch(() => {
      if (!active) return;
      enqueueSnackbar('Entrega não encontrada', { variant: 'error' });
      onClose();
    }).finally(() => {
      if (active) setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [open, callId]);

  const fillByCep = async (rawCep: string, index: number, setFieldValue: FormikProps<FormValues>['setFieldValue']) => {
    const cep = rawCep?.replace(/[^0-9]/g, '');
    if (cep?.length !== 8) return;
    try {
      const data = await viacep(cep);
      if (data.erro) throw new Error('CEP inválido');
      setFieldValue(`destinations.${index}.street`, data.logradouro ?? '');
      setFieldValue(`destinations.${index}.neighborhood`, data.bairro ?? '');
      setFieldValue(`destinations.${index}.city`, data.localidade ?? '');
      setFieldValue(`destinations.${index}.state`, data.uf ?? '');
    } catch (error: any) {
      enqueueSnackbar(error.message ?? 'CEP inválido', { variant: 'error' });
    }
  };

  const handleClose = () => {
    if (savingIndex !== null || recalculatingIndex !== null) return;
    onClose();
  };

  const addressKeyOf = (destination: DestinationForm) => [
    destination.street,
    destination.number,
    destination.neighborhood,
    destination.city,
    destination.state
  ].map((part) => (part ?? '').trim().toUpperCase()).join('|');

  const recalculateDestination = async (
    index: number,
    destination: DestinationForm,
    setFieldValue: FormikProps<FormValues>['setFieldValue']
  ) => {
    if (!canEdit || savingIndex !== null || recalculatingIndex !== null) return;
    const missing = !destination.street.trim()
      || !destination.number.trim()
      || !destination.neighborhood.trim()
      || !destination.city.trim()
      || !destination.state.trim();
    if (missing || destination.state.trim().length !== 2) {
      enqueueSnackbar('Preencha logradouro, número, bairro, cidade e UF', { variant: 'warning' });
      return;
    }

    if (!clientId || !driverCategory) {
      enqueueSnackbar('Não foi possível identificar o cliente ou a categoria da entrega', { variant: 'warning' });
      return;
    }

    setRecalculatingIndex(index);
    try {
      const found = await getAddressByGoogle({
        street: destination.street,
        number: destination.number,
        neighborhood: destination.neighborhood,
        city: destination.city,
        state: destination.state,
        zipCode: destination.zipCode,
        complement: destination.complement,
        ibge: null,
        location: null
      } as Address);
      if (!found?.geometry?.location) {
        enqueueSnackbar('Não foi possível localizar o endereço. Verifique os dados.', { variant: 'error' });
        return;
      }
      const quote = await callService.recalculateDestination({
        clientId,
        driverCategory,
        placeId: found.placeId || null,
        street: destination.street.trim(),
        number: destination.number.trim(),
        neighborhood: destination.neighborhood.trim(),
        city: destination.city.trim(),
        state: destination.state.trim().toUpperCase(),
        latitude: found.geometry.location.lat,
        longitude: found.geometry.location.lng,
        comments: destination.comments.trim() ? destination.comments.trim() : null,
        complement: destination.complement.trim(),
        receiverName: destination.receiverName,
        receiverPhone: destination.receiverPhone
      });
      setFieldValue(`destinations.${index}.value`, toMoney(quote.value));
      setFieldValue(`destinations.${index}.valueBack`, toMoney(quote.valueBack));
      setFieldValue(`destinations.${index}.subsidiaryFeeValue`, toMoney(quote.subsidiaryFeeValue));
      setFieldValue(`destinations.${index}.subsidiaryFeeValueBack`, toMoney(quote.subsidiaryFeeValueBack));
      setFieldValue(`destinations.${index}.distanceInfo`, formatRouteDistance(quote));
      setFieldValue(`destinations.${index}.durationInfo`, formatRouteDuration(quote));
      setRouteQuote({
        addressKey: addressKeyOf({
          ...destination,
          state: destination.state.trim().toUpperCase()
        }),
        distance: quote.distance,
        distanceInfo: quote.distanceInfo,
        duration: quote.duration,
        durationInfo: quote.durationInfo,
        latitude: quote.latitude,
        longitude: quote.longitude,
        placeId: quote.placeId
      });
      enqueueSnackbar('Valores recalculados. Salve para gravar.', { variant: 'info' });
    } catch (error: any) {
      enqueueSnackbar(error.message ?? 'Não foi possível recalcular a entrega', { variant: 'error' });
    } finally {
      setRecalculatingIndex(null);
    }
  };

  const saveDestination = async (
    index: number,
    destination: DestinationForm,
    setFieldValue: FormikProps<FormValues>['setFieldValue']
  ) => {
    if (!canEdit || savingIndex !== null || recalculatingIndex !== null) return;
    const original = baseline[index] ?? emptyDestination();
    const locationChanged = !sameText(destination.street, original.street)
      || !sameText(destination.number, original.number)
      || !sameText(destination.neighborhood, original.neighborhood)
      || !sameText(destination.city, original.city)
      || destination.state.trim().toUpperCase() !== original.state.trim().toUpperCase();
    const complementChanged = !sameText(destination.complement, original.complement);
    const commentsChanged = !sameText(destination.comments, original.comments);
    const valuesChanged = !sameMoney(destination.value, original.value)
      || !sameMoney(destination.valueBack, original.valueBack)
      || !sameMoney(destination.subsidiaryFeeValue, original.subsidiaryFeeValue)
      || !sameMoney(destination.subsidiaryFeeValueBack, original.subsidiaryFeeValueBack);
    const hasBackChanged = hasBack !== savedHasBack;

    if (!locationChanged && !complementChanged && !commentsChanged && !valuesChanged && !hasBackChanged) {
      enqueueSnackbar('Nenhuma alteração neste destino', { variant: 'info' });
      return;
    }

    const quoted = routeQuote?.addressKey === addressKeyOf(destination) ? routeQuote : null;
    if (locationChanged) {
      const missing = !destination.street.trim()
        || !destination.number.trim()
        || !destination.neighborhood.trim()
        || !destination.city.trim()
        || !destination.state.trim();
      if (missing || destination.state.trim().length !== 2) {
        enqueueSnackbar('Preencha logradouro, número, bairro, cidade e UF', { variant: 'warning' });
        return;
      }
      if (!quoted || !Number.isFinite(quoted.latitude) || !Number.isFinite(quoted.longitude)) {
        enqueueSnackbar('Recalcule o destino antes de salvar o novo endereço', { variant: 'warning' });
        return;
      }
    }

    setSavingIndex(index);
    try {
      const result = await callService.updateDestinationAddress({
        id: callId,
        index,
        daysAllowed,
        patch: {
          comments: destination.comments.trim() ? destination.comments.trim() : null,
          complement: destination.complement.trim(),
          updateLocation: locationChanged,
          address: locationChanged ? formatDestinationAddress(destination) : undefined,
          street: destination.street.trim(),
          number: destination.number.trim(),
          neighborhood: destination.neighborhood.trim(),
          city: destination.city.trim(),
          state: destination.state.trim().toUpperCase(),
          placeId: quoted?.placeId,
          latitude: quoted?.latitude,
          longitude: quoted?.longitude,
          hasBack,
          value: toMoney(destination.value),
          valueBack: toMoney(destination.valueBack),
          subsidiaryFeeValue: toMoney(destination.subsidiaryFeeValue),
          subsidiaryFeeValueBack: toMoney(destination.subsidiaryFeeValueBack),
          distance: quoted?.distance,
          distanceInfo: quoted?.distanceInfo,
          duration: quoted?.duration,
          durationInfo: quoted?.durationInfo
        }
      });

      const next = {
        ...destination,
        street: destination.street.trim(),
        number: destination.number.trim(),
        neighborhood: destination.neighborhood.trim(),
        city: destination.city.trim(),
        state: destination.state.trim().toUpperCase(),
        complement: destination.complement.trim(),
        comments: destination.comments.trim(),
        currentAddress: locationChanged ? formatDestinationAddress(destination) : destination.currentAddress,
        value: typeof result.value === 'number' ? result.value : toMoney(destination.value),
        valueBack: typeof result.valueBack === 'number' ? result.valueBack : toMoney(destination.valueBack),
        subsidiaryFeeValue: typeof result.subsidiaryFeeValue === 'number' ? result.subsidiaryFeeValue : toMoney(destination.subsidiaryFeeValue),
        subsidiaryFeeValueBack: typeof result.subsidiaryFeeValueBack === 'number' ? result.subsidiaryFeeValueBack : toMoney(destination.subsidiaryFeeValueBack)
      };
      setBaseline((current) => current.map((item, itemIndex) => (itemIndex === index ? next : item)));
      setFieldValue(`destinations.${index}.street`, next.street);
      setFieldValue(`destinations.${index}.number`, next.number);
      setFieldValue(`destinations.${index}.neighborhood`, next.neighborhood);
      setFieldValue(`destinations.${index}.city`, next.city);
      setFieldValue(`destinations.${index}.state`, next.state);
      setFieldValue(`destinations.${index}.complement`, next.complement);
      setFieldValue(`destinations.${index}.comments`, next.comments);
      setFieldValue(`destinations.${index}.currentAddress`, next.currentAddress);
      setFieldValue(`destinations.${index}.value`, next.value);
      setFieldValue(`destinations.${index}.valueBack`, next.valueBack);
      setFieldValue(`destinations.${index}.subsidiaryFeeValue`, next.subsidiaryFeeValue);
      setFieldValue(`destinations.${index}.subsidiaryFeeValueBack`, next.subsidiaryFeeValueBack);
      setSavedHasBack(hasBack);
      if (locationChanged) onUpdated?.(index, next.currentAddress);
      if (typeof result.paymentValue === 'number') setPaymentValue(result.paymentValue);
      if (result.synced) {
        const valueLabel = locationChanged && typeof result.paymentValue === 'number'
          ? ` Novo valor: ${result.paymentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`
          : '';
        enqueueSnackbar(`Destino atualizado.${valueLabel}`, { variant: 'success' });
      } else {
        enqueueSnackbar('Alteração salva. A listagem pode demorar a atualizar.', { variant: 'warning' });
      }
    } catch (error: any) {
      enqueueSnackbar(error.message ?? 'Ops, aconteceu algo inesperado, por favor tente novamente', { variant: 'error' });
    } finally {
      setSavingIndex(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <div className="font-weight-bold">{clientName || 'Entrega'}</div>
            <span className="text-black-50">
              {createdAt && !Number.isNaN(createdAt.getTime()) ? toBrazilDateTime(createdAt) : ''}
              {statusLabel ? ` · ${statusLabel}` : ''}
              {typeof paymentValue === 'number' ? ` · ${paymentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : ''}
            </span>
          </Box>
          <IconButton onClick={handleClose} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {!isLoading && !eligible && (
          <Alert severity="warning" className="mb-3">
            Só é possível ajustar entregas faturadas com um único destino.
          </Alert>
        )}
        {!isLoading && eligible && createdAt && !Number.isNaN(createdAt.getTime()) && !withinWindow && (
          <Alert severity="warning" className="mb-3">
            {`O prazo para ajuste desta entrega encerrou (${daysAllowed} dias). Os dados estão somente para consulta.`}
          </Alert>
        )}
        {isLoading && <Loading />}
        {!isLoading && (
          <Formik enableReinitialize initialValues={initialValues} onSubmit={() => undefined}>
            {(props: FormikProps<FormValues>) => {
              const {
                values, handleChange, handleBlur, setFieldValue
              } = props;
              return (
                <Form noValidate autoComplete="off">
                  {values.destinations.map((destination, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Card className="card-box mb-4" key={`${callId}-destination-${index}`}>
                      <CardContent>
                        <h5 className="font-weight-bold mb-1">{`Destino ${index + 1}`}</h5>
                        <p className="text-black-50 mb-1">
                          {destination.receiverName || 'Recebedor não informado'}
                          {' / '}
                          {destination.receiverPhone ? phoneNumber(destination.receiverPhone) : 'Telefone não informado'}
                        </p>
                        {destination.currentAddress && (
                          <p className="mb-3">{destination.currentAddress}</p>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="CEP"
                              name={`destinations.${index}.zipCode`}
                              value={destination.zipCode}
                              onChange={handleChange}
                              onBlur={(event) => fillByCep(event.target.value, index, setFieldValue)}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                              InputProps={{ inputComponent: CepMask as any }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Logradouro"
                              name={`destinations.${index}.street`}
                              value={destination.street}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Número"
                              name={`destinations.${index}.number`}
                              value={destination.number}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8} md={4}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Bairro"
                              name={`destinations.${index}.neighborhood`}
                              value={destination.neighborhood}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Cidade"
                              name={`destinations.${index}.city`}
                              value={destination.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3} md={2}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="UF"
                              name={`destinations.${index}.state`}
                              value={destination.state}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                              inputProps={{ maxLength: 2 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={9} md={2}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Complemento"
                              name={`destinations.${index}.complement`}
                              value={destination.complement}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Distância"
                              value={destination.distanceInfo || 'Não calculada'}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Tempo"
                              value={destination.durationInfo || 'Não calculado'}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3} className="d-flex align-items-center">
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  color="primary"
                                  checked={hasBack}
                                  onChange={(event) => setHasBack(event.target.checked)}
                                  disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                                />
                              )}
                              label="Com retorno"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Valor"
                              name={`destinations.${index}.value`}
                              value={destination.value}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                              InputProps={{ inputComponent: CurrencyFormatCustom as any }}
                            />
                          </Grid>
                          {hasBack && (
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label="Retorno"
                                name={`destinations.${index}.valueBack`}
                                value={destination.valueBack}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                                InputProps={{ inputComponent: CurrencyFormatCustom as any }}
                              />
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Taxa da franquia"
                              name={`destinations.${index}.subsidiaryFeeValue`}
                              value={destination.subsidiaryFeeValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                              InputProps={{ inputComponent: CurrencyFormatCustom as any }}
                            />
                          </Grid>
                          {hasBack && (
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                label="Taxa de retorno"
                                name={`destinations.${index}.subsidiaryFeeValueBack`}
                                value={destination.subsidiaryFeeValueBack}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                                InputProps={{ inputComponent: CurrencyFormatCustom as any }}
                              />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              variant="outlined"
                              label="Observações"
                              name={`destinations.${index}.comments`}
                              value={destination.comments}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                            />
                          </Grid>
                          <Grid item xs={12} className="text-right">
                            <Button
                              variant="outlined"
                              color="primary"
                              className="mr-2"
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                              onClick={() => recalculateDestination(index, destination, setFieldValue)}
                            >
                              {recalculatingIndex === index ? 'Recalculando...' : 'Recalcular'}
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={!canEdit || savingIndex !== null || recalculatingIndex !== null}
                              onClick={() => saveDestination(index, destination, setFieldValue)}
                            >
                              {savingIndex === index ? 'Salvando...' : 'Salvar destino'}
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  {values.destinations.length === 0 && (
                    <Card className="card-box mb-4">
                      <CardContent>Esta entrega não possui destinos.</CardContent>
                    </Card>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

const page = () => {
  const history = useHistory();
  const { id } = useParams<ParamTypes>();
  const { isReadOnly } = useAuth();

  if (isReadOnly) return <Redirect to="/deliveries" />;

  return (
    <AdjustmentForm
      open
      callId={id}
      onClose={() => history.push('/deliveries/edit')}
      onUpdated={() => undefined}
    />
  );
};

export default page;
